/**
 * Informe para Inspección de Trabajo (RD 8/2019).
 *
 * La pantalla que justifica el producto entero: elegir mes, descargar el PDF.
 * El registro completo, inmutable y verificable — fichajes, correcciones con
 * su traza, horas extra y complementarias.
 */
import { useCallback, useEffect, useState } from 'react';
import { AdminLayout } from './AdminLayout';
import {
  AdminUser,
  downloadLegalReport,
  leerErrorDeBlob,
  getUsers,
} from '../../services/apiClient';
import { Icon } from '../ui/Icon';

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

/**
 * Últimos meses, el actual primero.
 *
 * Eran 12, y la conservación legal del RD 8/2019 es de CUATRO AÑOS: si la
 * Inspección pedía en 2027 los registros de 2024, el admin no podía generarlos
 * desde la pantalla aunque los datos siguieran en producción.
 */
function lastMonths(n = 48): { year: number; month: number; label: string }[] {
  const out = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}`,
    });
  }
  return out;
}

export function InspectionReport() {
  const options = lastMonths();
  const [selected, setSelected] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workers, setWorkers] = useState<AdminUser[]>([]);
  const [workerId, setWorkerId] = useState<number | 'ALL'>('ALL');

  useEffect(() => {
    // Sin filtro de is_active: el registro del art. 34.9 se conserva 4 años
    // (RD 8/2019) y el caso típico de reclamación es el de quien YA SE FUE.
    // El backend no restringe; filtrar aquí hacía imposible emitir el informe
    // de un ex-trabajador y "Toda la plantilla" entregaba un registro
    // incompleto creído completo. Los de baja van marcados ("· baja") como en
    // WorkersAdmin.
    getUsers()
      .then((us) => setWorkers(us))
      .catch(() => setError('No se pudo cargar la plantilla'));
  }, []);

  const descargarUno = useCallback(
    async (user: AdminUser, year: number, month: number) => {
      const blob = await downloadLegalReport(year, month, user.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const quien = user.employee_code || `${user.first_name}-${user.last_name}`;
      a.download = `kakuat-registro-${quien}-${year}-${String(month).padStart(2, '0')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    },
    [],
  );

  const download = async () => {
    const { year, month, label } = options[selected];
    const objetivo =
      workerId === 'ALL' ? workers : workers.filter((u) => u.id === workerId);

    if (objetivo.length === 0) {
      setError('No hay trabajadores que incluir en el informe');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      // El registro del art. 34.9 es POR TRABAJADOR: un PDF cada uno.
      for (const u of objetivo) {
        await descargarUno(u, year, month);
      }
    } catch (e) {
      const detalle = await leerErrorDeBlob(e);
      setError(detalle ?? `No se pudo generar el informe de ${label}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout title="Informe para Inspección">
      <div className="max-w-[820px]">
        <div className="rounded-2xl bg-surface border border-line-strong p-6 md:p-9">
          <span className="inline-flex items-center gap-2 font-mono text-[12px] text-brand bg-brand-soft border border-line-strong rounded-full px-3 py-1.5 mb-4.5 mb-5">
            RD 8/2019 · registro obligatorio
          </span>
          <h2 className="font-display font-extrabold text-[clamp(24px,3vw,34px)] tracking-tight mb-2.5">
            El registro completo de un mes
          </h2>
          <p className="text-ink-2 text-[15.5px] max-w-[56ch] mb-6">
            El registro de jornada de cada trabajador, día a día, listo para
            entregar a Inspección de Trabajo.
          </p>

          {error && (
            <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14px] mb-4">{error}</p>
          )}

          <div className="flex flex-wrap items-end gap-3.5">
            <label className="block">
              <span className="label">Trabajador</span>
              <select
                className="field min-w-[220px]"
                value={String(workerId)}
                onChange={(e) =>
                  setWorkerId(e.target.value === 'ALL' ? 'ALL' : Number(e.target.value))
                }
              >
                <option value="ALL">Toda la plantilla ({workers.length})</option>
                {workers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {`${u.first_name} ${u.last_name}${u.is_active ? '' : ' · baja'}`}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="label">Mes</span>
              <select
                className="field min-w-[180px]"
                value={selected}
                onChange={(e) => setSelected(Number(e.target.value))}
              >
                {options.map((o, i) => (
                  <option key={o.label} value={i}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              onClick={download}
              disabled={busy}
              className="h-12 px-5.5 px-6 rounded-xl bg-brand text-on-brand font-bold text-[15px] flex items-center gap-2.5 disabled:opacity-50"
            >
              <Icon name="file" size={18} strokeWidth={1.9} />
              {busy ? 'Generando…' : 'Descargar PDF'}
            </button>
          </div>

          <div className="border-t border-line mt-6 pt-5 font-mono text-[12px] text-ink-2">
            {/* El copy prometía "correcciones con su traza" y "resumen art.
                35.5" y el PDF no lleva ninguna de las dos: es fecha, entrada,
                salida, total y estado por día. Prometer en la pantalla lo que
                el papel no trae es lo mismo que imprimir un dato falso. */}
            <p>
              · un PDF por trabajador · fecha, entrada, salida, total y estado de
              cada día · conservación 4 años (RD 8/2019)
            </p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
